import {
  Box,
  Button,
  MenuItem,
  Snackbar,
  TextField,
  Alert,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getUserResponses, submitResponses } from '../../utils/mutations/form';
import { Question, User } from '../../utils/types';

interface UserFormProps {
  confCode: string;
  user: User;
  questions: Question[];
  onSubmit?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ confCode, user, questions, onSubmit }) => {
  const [responses, setResponses] = useState<{ [questionId: string]: string }>({});
  
  const [snackbarState, setsnackbarState] = useState<number>(0); // 0: closed, 1: open, 2: error
  const [snackbarMessage, setSnackbarMessage] = useState<string>();

  const handleInputChange = (questionId: string, value: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const userResponses = await getUserResponses(user.uid);
        setResponses(userResponses);
      } catch (error) {
        console.error('Error fetching user responses:', error);
      }
    }
    fetchResponses();
  }, [user.uid]);


  const handleSubmit = async () => {
    try {
      if (!questions || questions.length === 0) {
        throw new Error('No questions available');
      }

      // Check for unanswered required questions
      const hasUnanswered = questions.some((question: Question) => {
        if (question.properties?.required) {
          const response = responses[question.id];
          if (question.format === 'multiplechoice') {
            return !response || response === '';
          }
          return !response || response.trim() === '';
        }
        return false;
      });
      
      if (hasUnanswered) {
        setSnackbarMessage('Please answer all required questions.');
        setsnackbarState(2);
        return;
      }

      await submitResponses(confCode, user.uid, responses);
      setSnackbarMessage('Responses saved successfully.');
      setsnackbarState(1);
      onSubmit?.();
    } catch (error: any) {
      console.error('Error saving responses:', error);
      alert(error.message || 'Failed to save responses. Please try again.');
    }
  };

  const renderForm = () => (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      width: '100%',
    }}>
      {questions.map((question: Question) => {
          let inputProps = {};
          let type = 'text';

          if (question.format === 'multiline') {
            inputProps = { multiline: true, rows: 4 };
          } else if (question.format === 'date') {
            inputProps = { InputLabelProps: { shrink: true } };
            type = 'date';
          } else if (question.format === 'number') {
            type = 'number';
          } else if (question.format === 'email') {
            type = 'email';
          } 
          
          if (question.format === 'hline') {
            return <Box key={question.id} sx={{ borderBottom: '1px solid #ffffff'}} />;
          } else if (question.format === 'multiplechoice') {
            return (
              <TextField
                select
                fullWidth
                variant="outlined"
                label={question.question}
                value={responses[question.id] || ''}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                required={question.properties?.required}
              >
                <MenuItem value="">Select an option</MenuItem>
                {(question.properties?.options || []).map((option: any) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )
          }
        
          return (
            <TextField
              key={question.id}
              label={question.question}
              type={type}
              value={responses[question.id] || ''}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              required={question.properties?.required}
              {...inputProps}
            />
          );
        })}
    </Box>
  )

  return (
    <Box sx={{ p: '0px 30px', maxHeight: '70vh', overflowY: 'auto' }}>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          width: '100%',
        }}
      >
        <Typography variant="h4" sx={{
          color: 'black',
          // fontWeight: 'bold',
          marginBottom: '10px',
        }}>Delegate Form</Typography>
        {renderForm()}
        <Button variant="contained" onClick={handleSubmit}>
          Submit
        </Button>
      </Box>
      <Snackbar
        open={snackbarState > 0}
        autoHideDuration={6000}
        onClose={() => setsnackbarState(0)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setsnackbarState(0)}
          severity={snackbarState === 1 ? 'success' : 'error'} // Set severity to 'error' for errors
          sx={{ width: '100%' }}
          
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserForm;
